import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import cx from 'classnames'

import Button from 'common/components/button'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import ErrorPage from 'common/components/error/ErrorPage'
import Footer from 'common/components/layout/Footer'
import Loader from 'common/components/loaders/Loader'
import PageHeader from 'common/components/PageHeader'
import PageTitle from 'common/components/PageTitle'
import { useDocumentTitle } from 'common/hooks/custom'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { ReactComponent as AddDefault } from 'common/icons/add/default.svg'
import {
  hasAppliedContentLibraryFilters,
  hideTemplates,
  showTemplates,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

import useDeleteContentMutation from '../api/mutations/useDeleteContentMutation'
import useGetContentLibraryQuery, {
  ContentLibraryEntry,
} from '../api/queries/useGetContentLibraryQuery'
import MainContent from '../components/MainContent'
import TemplateSelection from '../components/templateSelection'

import DeleteConfirmationModal from './components/DeleteConfirmationModal'
import Filters from './components/Filters'
import SearchInput from './components/SearchInput'
import ContentLibraryTable from './ContentLibraryTable'
import EmptyState from './EmptyState'
import { ContentLibrarySortBy } from './interfaces'

import styles from './ContentsPage.module.css'

export const CONTENTS_PAGE_VIEW_KEY = `unified-flow-page-view`
const DEFAULT_PAGE_SIZE = 10

const ContentsPage = () => {
  const dispatch = useAppDispatch()
  const history = useHistory<{ templateTab?: string; contentTab?: string }>()
  const isNewContent = useAppSelector(
    (state) => state.unifiedFlow.showTemplateView
  )
  useDocumentTitle(
    `${isNewContent ? 'Templates' : 'Content Library'} | Jacquard`
  )

  const { location } = history

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortBy, setSortBy] = useState<ContentLibrarySortBy>()
  const [selectedTemplateTab, setSelectedTemplateTab] = useState<string>('all')
  const [searchContentValue, setSearchContentValue] = useState<string>('')
  const [isConfirmTemplateModalShown, setIsConfirmTemplateModalShown] =
    useState(false)

  const deleteContentMutation = useDeleteContentMutation()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const [contentIdToDelete, setContentIdToDelete] = useState<string>('')
  const hasFilters = useAppSelector(hasAppliedContentLibraryFilters)

  const { data, status, isFetching } = useGetContentLibraryQuery({
    offset: page * pageSize,
    limit: pageSize,
    sortBy: sortBy,
    searchValue: searchContentValue,
  })

  const contentData = useMemo(() => data?.data ?? [], [data?.data])
  const pinnedContentData = useMemo(
    () => data?.pinnedContents ?? [],
    [data?.pinnedContents]
  )
  const totalItems = useMemo(
    () => Math.max((data?.total ?? 0) - pinnedContentData.length, 0),
    [data?.total, pinnedContentData.length]
  )
  const nonPinnedContentData = useMemo(
    () => contentData.filter((item) => !item.isPinned),
    [contentData]
  )

  const resetPagination = useCallback(() => {
    setPage(0)
  }, [])

  const templatesToAdd = useAppSelector(
    (state) => state.unifiedFlow.templatesToAdd
  )

  const closeModal = () => setContentIdToDelete('')

  useEffect(() => {
    const { templateTab } = location.state ?? {}

    if (templateTab) {
      dispatch(showTemplates())
      setSelectedTemplateTab(templateTab)
    }
  }, [location, dispatch])

  const content = useCallback(
    (content: ContentLibraryEntry[]) => {
      if (content.length === 0 && pinnedContentData.length === 0) {
        if (searchContentValue !== '') {
          return (
            <div
              className="text-5xl font-bold text-coolGray-800 leading-normal"
              data-testid="noContentItems"
            >
              There are no content items matching your search, please try again
            </div>
          )
        }
        return (
          <div
            className="text-5xl font-bold text-coolGray-800 leading-normal"
            data-testid="noResults"
          >
            There are no results matching that search.
          </div>
        )
      } else {
        return (
          <ContentLibraryTable
            content={nonPinnedContentData}
            pinnedContents={pinnedContentData}
            onDelete={setContentIdToDelete}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            totalItems={totalItems}
            isLoading={isFetching}
            searchValue={searchContentValue}
            sortBy={sortBy}
            onSortChange={(newSortByRules: ContentLibrarySortBy[]) => {
              const newRule = newSortByRules[0]

              if (newRule) {
                const transformedRule: ContentLibrarySortBy = {
                  id: newRule.id === 'Send Date' ? 'sendDate' : newRule.id,
                  desc: newRule.desc,
                }

                if (
                  sortBy?.id !== transformedRule.id ||
                  sortBy?.desc !== transformedRule.desc
                ) {
                  setSortBy(transformedRule)
                  resetPagination()
                }
              } else {
                if (sortBy !== undefined) {
                  setSortBy(undefined)
                  resetPagination()
                }
              }
            }}
          />
        )
      }
    },
    [
      searchContentValue,
      nonPinnedContentData,
      pinnedContentData,
      page,
      pageSize,
      totalItems,
      isFetching,
      sortBy,
      resetPagination,
    ]
  )

  const getState = () => {
    if (status === 'loading' && contentData.length === 0) {
      return 'loading'
    } else if (status === 'error') {
      return 'error'
    } else if (
      status === 'success' &&
      contentData.length === 0 &&
      pinnedContentData.length === 0 &&
      !hasFilters &&
      !searchContentValue
    ) {
      return 'empty'
    } else {
      return 'success'
    }
  }

  const handleTemplateSelectionClose = () => {
    if (templatesToAdd.length > 0) {
      setIsConfirmTemplateModalShown(true)
    } else {
      dispatch(hideTemplates())
    }
  }

  const state = getState()

  return (
    <>
      <MainContent className="lg:px-40 px-6 pt-6 ">
        {
          {
            empty: <EmptyState />,
            success: (
              <div className="flex flex-col w-full h-full max-w-screen-lg sm:mx-auto">
                <PageHeader className="mb-4">
                  <PageTitle
                    className="text-4xl font-medium text-base-700"
                    title="Content Library"
                  />
                </PageHeader>

                <div className="flex justify-between items-stretch relative">
                  <div className={cx('w-full', styles.contentPageTabs)}>
                    <div className="mb-8 flex gap-4 justify-between items-start">
                      <Filters onFilterChange={resetPagination} />
                      <div className="flex flex-none relative top-6">
                        <Button
                          data-cy="add-content-button"
                          data-testid="add-content-button"
                          aria-label="Create content"
                          variant="primary"
                          onClick={() => dispatch(showTemplates())}
                          className="uppercase mr-4"
                          prefixIcon={<AddDefault width={24} height={24} />}
                        >
                          Create
                        </Button>
                        <SearchInput
                          onSearch={(value) => {
                            resetPagination()
                            setSearchContentValue(value)
                          }}
                        />
                      </div>
                    </div>

                    {content(nonPinnedContentData)}
                  </div>
                </div>
                <Footer />
              </div>
            ),
            loading: (
              <div className="w-full">
                <Loader data-testid="contents-loader" />
              </div>
            ),
            error: <ErrorPage />,
          }[state]
        }
      </MainContent>
      <TemplateSelection
        isVisible={isNewContent}
        onClose={handleTemplateSelectionClose}
        intialSelectedTab={selectedTemplateTab}
      />
      {contentIdToDelete && (
        <DeleteConfirmationModal
          onCancel={closeModal}
          onConfirm={() => {
            deleteContentMutation.mutate({
              accountId,
              contentId: contentIdToDelete,
            })
            closeModal()
          }}
        />
      )}
      <ConfirmationModal
        open={isConfirmTemplateModalShown}
        title="Hold up!"
        data-cy="template-selection-confirmation-modal"
        data-testid="template-selection-confirmation-modal"
        confirmationText="You're about to leave the page and any work carried out will be lost. Are you sure you want to do this?"
        cancelButtonText="Leave without saving"
        confirmButtonText="Keep editing"
        confirmButtonVariant="primary"
        onConfirm={() => setIsConfirmTemplateModalShown(false)}
        onCancel={() => {
          setIsConfirmTemplateModalShown(false)
          dispatch(hideTemplates())
        }}
      />
    </>
  )
}

export default ContentsPage
